import {
  NestedListWrapper,
  SecuredListLink,
  SecurityProvider
} from "lego-gui-lib/lib";
import React from "react";
import { useTranslation } from "react-i18next";
import { Auth } from "../../../core/model/auth";
import {
  AccountTreeTwoTone,
  AssignmentTwoTone,
  CancelTwoTone,
  CheckCircleTwoTone,
  EventNoteTwoTone,
  LanguageTwoTone,
  ListAltTwoTone,
  PeopleAltTwoTone,
  PermPhoneMsgTwoTone,
  PhonelinkSetupTwoTone,
  PublicTwoTone,
  SecurityTwoTone,
  SettingsInputAntennaTwoTone,
  TuneTwoTone
} from "@material-ui/icons";

export const SideMenuBody: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const items = [
    // ADMIN Access menu:
    // ------------------
    // Customers
    {
      key: "customers",
      to: "/admin/customers",
      title: t("hns:customer:domain_plural", { count: 2 }),
      security: [
        Auth.Roles.ADMIN,
        Auth.Roles.SAN_ADMIN,
        Auth.Roles.SAN_ADMIN_READ_ONLY,
        Auth.Roles.CUSTOMER_OWNER
      ],
      icon: PeopleAltTwoTone
    },
    // SANs
    {
      key: "SANs",
      to: "/admin/sans",
      title: t("hns:san:domain_plural"),
      security: Auth.Roles.ANYONE,
      icon: PhonelinkSetupTwoTone
    },
    // SAN matchings
    {
      key: "sanMatchings",
      to: "/admin/sanMatchings",
      title: t("hns:sanMatching:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: AccountTreeTwoTone
    },
    // Geographic areas (new dir)
    {
      key: "geographic areas",
      title: t("hns:area:sideMenuLabel"),
      security: Auth.Roles.ANYONE,
      icon: PublicTwoTone,
      nested: [
        // 	Areas
        {
          key: "areas",
          to: "/admin/areas",
          title: t("hns:area:domain_plural"),
          security: Auth.Roles.ANYONE
        },
        // 	Area splittings
        {
          key: "areaSplittings",
          to: "/admin/areaSplittings",
          title: t("hns:areaSplitting:domain_plural"),
          security: Auth.Roles.ANYONE
        }
      ]
    },
    // Lists
    {
      key: "lists",
      to: "/admin/lists",
      title: t("hns:list:domain_plural"),
      security: Auth.Roles.ANYONE,
      icon: ListAltTwoTone
    },
    // Messages
    {
      key: "messages",
      to: "/admin/messages",
      title: t("hns:message:domain_plural"),
      security: Auth.Roles.ANYONE,
      icon: PermPhoneMsgTwoTone
    },
    // Network operators
    {
      key: "networkOperators",
      to: "/admin/networkOperator",
      title: t("hns:networkOperator:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: SettingsInputAntennaTwoTone
    },
    // Calendars
    {
      key: "calendars",
      to: "/admin/calendars",
      title: t("hns:calendar:domain_plural"),
      security: Auth.Roles.ANYONE,
      icon: EventNoteTwoTone
    },
    // Languages
    {
      key: "languages",
      to: "/admin/languages",
      title: t("hns:language:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: LanguageTwoTone
    },
    // Language selections
    {
      key: "languageSelections",
      to: "/admin/languageSelections",
      title: t("hns:languageSelect:domain_plural"),
      security: Auth.Roles.ANYONE,
      icon: () => (
        <>
          <LanguageTwoTone
            style={{
              color: "#b3b1b1",
              marginLeft: "-4"
            }}
          />
          <LanguageTwoTone
            style={{
              marginLeft: "-18px"
            }}
          />
        </>
      )
    },
    // Web access (new dir)
    {
      key: "webAccess",
      title: t("hns:auth:webAccess"),
      security: Auth.Roles.ANY_ADMIN,
      icon: SecurityTwoTone,
      nested: [
        // 	External web users
        {
          key: "webUsers",
          to: "/admin/webUsers",
          title: t("hns:webUser:domain_plural"),
          security: Auth.Roles.ANY_ADMIN
        },
        // 	Web users roles
        {
          key: "webUserRoles",
          to: "/admin/webUserRoles",
          title: t("hns:webUserRole:domain_plural"),
          security: Auth.Roles.ANY_ADMIN
        },
        // 	Internal SSO role mappings
        {
          key: "webUserRoleMappings",
          to: "/admin/webUserRoleMappings",
          title: t("hns:webUserRoleMapping:domain_plural"),
          security: Auth.Roles.ANY_ADMIN
        }
      ]
    },
    // Report definitions
    {
      key: "reportDefinitions",
      to: "/admin/reportDefinitions",
      title: t("hns:reportDefinition:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: AssignmentTwoTone
    },
    // Service profiles
    {
      key: "serviceProfiles",
      to: "/admin/serviceProfiles",
      title: t("hns:serviceProfile:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: CheckCircleTwoTone
    },
    // Baring matrixes
    {
      key: "barringMatrix",
      to: "/admin/barringMatrixes",
      title: t("hns:barringMatrix:domain_plural"),
      security: Auth.Roles.ANY_ADMIN,
      icon: CancelTwoTone
    },
    // Properties
    {
      key: "properties",
      to: "/admin/properties",
      title: t("hns:property:domain_plural"),
      security: Auth.Roles.PLATFORM_ADMIN,
      icon: TuneTwoTone
    }
  ];

  return (
    <div>
      {items.map(element => {
        return (
          <SecurityProvider
            security={{ readableRole: element.security }}
            key={element.key}
          >
            {element.to && (
              <SecuredListLink
                icon={element.icon || (() => null)}
                requiredAccess={"readable"}
                isNested={false}
                to={element.to}
              >
                {element.title}
              </SecuredListLink>
            )}
            {!element.to && element.nested && (
              <NestedListWrapper
                icon={element.icon || (() => null)}
                key={element.key}
                nestedListItems={element.nested!}
                text={element.title}
              />
            )}
          </SecurityProvider>
        );
      })}
    </div>
  );
};
